import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getKeyFromDataFieldWithDataType } from '../../../../common-types';
import type { CachedMasterDataService } from '../../../common/api/cached-master-data-service';
import { CONFIDENTIAL_DISPLAY_KEY } from '../../../common/constants/constants';
import type { Months as LegacyMonths } from '../../../common/date-manager/date-manager-constants';
import type { EmployeeService } from '../../../common/v2/api/employee/service';
import type { TimePointType } from '../../../common/v2/api/timeranges/types';
import type { VersionId } from '../../../common/v2/api/types';
import type {
  Months,
  TimeSliderState,
} from '../../../common/v2/common/components/timeslider/types';
import { useFieldLabelFormatter } from '../../../common/v2/common/hooks';
import { getMomentDate } from '../../../common/v2/date/date-utils';
import type { DateAsYYYYMMDD } from '../../../common/v2/types';
import { employeeToEnrichmentEntry } from './drilldownColumns';
import { getEmployeesForPbsDrillDown } from './services/getEmployeesForPbsDrillDown';
import {
  formatValueFilterFilenameSuffix,
  type PbsDrillDownValueFilter,
} from './services/pbsDrillDownValueFilter';
import type { DrillDownColumn, EnrichmentCacheEntry } from './types';

const BATCH_SIZE = 500;

function formatTimePointForFilename(tp: TimePointType): DateAsYYYYMMDD {
  if (tp instanceof Date || typeof tp === 'number') {
    return getMomentDate(tp).format('YYYYMM');
  }
  // Quarter: { year, quarterOfYear }
  return `${tp.year}Q${tp.quarterOfYear}`;
}

function formatApiDateForFilename(d: DateAsYYYYMMDD): DateAsYYYYMMDD {
  return getMomentDate(d).format('YYYYMM');
}

function sanitizeForFilename(label: string): string {
  return label.replace(/[\\/:*?"<>|\s]+/g, '_').replace(/^_+|_+$/g, '');
}

// ISO 8601 uses '/' for intervals; '--' is the filename-safe equivalent.
function joinDateRange(
  start: DateAsYYYYMMDD,
  end: DateAsYYYYMMDD
): DateAsYYYYMMDD {
  return start === end ? start : `${start}--${end}`;
}

function buildCsvFilename(
  measureLabel: string | undefined,
  cellDateRange: [DateAsYYYYMMDD, DateAsYYYYMMDD] | null | undefined,
  timeSliderState: TimeSliderState | null | undefined,
  valueFilter?: PbsDrillDownValueFilter | null,
  formatMetricValue?: (raw: number) => string
): string {
  const metricPart = measureLabel
    ? sanitizeForFilename(measureLabel) + '_'
    : '';
  // Prefer the period the drilled-into cell represents; fixed columns (e.g.
  // Period Total) carry no cell range, so fall back to the overall slider span.
  let dateRangePart = '';
  if (cellDateRange) {
    dateRangePart = `_${joinDateRange(
      formatApiDateForFilename(cellDateRange[0]),
      formatApiDateForFilename(cellDateRange[1])
    )}`;
  } else if (timeSliderState) {
    dateRangePart = `_${joinDateRange(
      formatTimePointForFilename(timeSliderState.start),
      formatTimePointForFilename(timeSliderState.end)
    )}`;
  }
  const valueFilterPart = formatValueFilterFilenameSuffix(
    valueFilter,
    formatMetricValue
  );
  return `${metricPart}underlying-employees${dateRangePart}${valueFilterPart}.csv`;
}

function escapeCsvValue(value: string): string {
  // Prefix values starting with formula-trigger characters to prevent CSV injection in Excel/Sheets.
  const sanitized = /^[=+\-@]/.test(value) ? `'${value}` : value;
  if (/[",\r\n]/.test(sanitized)) {
    return `"${sanitized.replace(/"/g, '""')}"`;
  }
  return sanitized;
}

export interface CsvMetricOpts {
  metricById: Record<string, number>;
  measureLabel?: string;
  formatMetricValue?: (v: number) => string;
}

interface UseExportCsvParams {
  sortedEmployeeIds: string[];
  /** Ordered columns to export, excluding the pinned Employee ID and the Value column. */
  columns: DrillDownColumn[];
  enrichmentCache: Record<string, EnrichmentCacheEntry>;
  setEnrichmentCache: Dispatch<
    SetStateAction<Record<string, EnrichmentCacheEntry>>
  >;
  csvMetricOpts: CsvMetricOpts | undefined;
  useDataViewEnrichment: boolean;
  endDate: string | null | undefined;
  firstMonthOfYear: Months | null | undefined;
  latestEmployeeVersion: VersionId | null | undefined;
  cachedMasterDataApiService: CachedMasterDataService | null | undefined;
  employeeService: EmployeeService;
  timeSliderState: TimeSliderState | null | undefined;
  /** Date range of the drilled-into cell; used in the export filename. */
  cellDateRange: [DateAsYYYYMMDD, DateAsYYYYMMDD] | null | undefined;
  /**
   * Active Value-column comparison filter. Rows are already narrowed via
   * `sortedEmployeeIds`; this is only for reflecting the filter in the filename.
   */
  valueFilter?: PbsDrillDownValueFilter | null;
}

export function useExportCsv({
  sortedEmployeeIds,
  columns,
  enrichmentCache,
  setEnrichmentCache,
  csvMetricOpts,
  useDataViewEnrichment,
  endDate,
  firstMonthOfYear,
  latestEmployeeVersion,
  cachedMasterDataApiService,
  employeeService,
  timeSliderState,
  cellDateRange,
  valueFilter = null,
}: UseExportCsvParams): {
  isExporting: boolean;
  exportProgress: { done: number; total: number } | null;
  handleExport: () => void;
} {
  const { t } = useTranslation();
  const formatFieldLabel = useFieldLabelFormatter();
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const accessibleFields = useMemo(
    () => columns.filter((c) => c.accessible).map((c) => c.field),
    [columns]
  );

  const downloadCsvWithCache = useCallback(
    (
      ids: string[],
      cache: Record<string, EnrichmentCacheEntry>,
      opts?: CsvMetricOpts
    ) => {
      const includeMetric =
        !!opts?.measureLabel &&
        !!opts.metricById &&
        Object.keys(opts.metricById).length > 0;
      const headers = [
        t('pages:dashboards.peopleBalanceSheet.cellDrillDown.rowNumber', {
          defaultValue: 'No.',
        }),
        t('pages:dashboards.peopleBalanceSheet.cellDrillDown.employeeId', {
          defaultValue: 'Employee ID',
        }),
        ...(includeMetric && opts!.measureLabel ? [opts!.measureLabel] : []),
        ...columns.map((c) => formatFieldLabel(c.field)),
      ];
      const confidential = t(CONFIDENTIAL_DISPLAY_KEY);
      const metricById = opts?.metricById;
      const rows = ids.map((id, index) => {
        const c = cache[id];
        const metricVal =
          includeMetric && metricById && metricById[id] != null
            ? opts?.formatMetricValue
              ? opts.formatMetricValue(metricById[id])
              : String(metricById[id])
            : '';
        return [
          escapeCsvValue(String(index + 1)),
          escapeCsvValue(id),
          ...(includeMetric ? [escapeCsvValue(metricVal)] : []),
          // A column the role can no longer read was never fetched, so there is nothing in
          // the cache to leak; it exports as CONFIDENTIAL to match what the table shows.
          ...columns.map((col) =>
            escapeCsvValue(
              col.accessible
                ? (c?.[getKeyFromDataFieldWithDataType(col.field)] ?? '')
                : confidential
            )
          ),
        ];
      });
      const csvContent = [
        headers.join(','),
        ...rows.map((r) => r.join(',')),
      ].join('\r\n');
      const encodedUri = encodeURIComponent('﻿' + csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodedUri);
      link.setAttribute(
        'download',
        buildCsvFilename(
          opts?.measureLabel,
          cellDateRange,
          timeSliderState,
          valueFilter,
          opts?.formatMetricValue
        )
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [t, timeSliderState, cellDateRange, columns, formatFieldLabel, valueFilter]
  );

  const handleExport = useCallback(() => {
    if (sortedEmployeeIds.length === 0 || isExporting) return;
    const missingIds = sortedEmployeeIds.filter((id) => !enrichmentCache[id]);
    if (missingIds.length === 0) {
      downloadCsvWithCache(sortedEmployeeIds, enrichmentCache, csvMetricOpts);
      return;
    }
    setIsExporting(true);
    setExportProgress({ done: 0, total: missingIds.length });
    const chunks: string[][] = [];
    for (let i = 0; i < missingIds.length; i += BATCH_SIZE) {
      chunks.push(missingIds.slice(i, i + BATCH_SIZE));
    }
    let completed = 0;
    const cacheForExport: Record<string, EnrichmentCacheEntry> = {
      ...enrichmentCache,
    };
    const runBatches = async () => {
      const useDataView =
        useDataViewEnrichment &&
        endDate &&
        firstMonthOfYear &&
        latestEmployeeVersion &&
        cachedMasterDataApiService;
      const firstMonthOfYearLegacy = firstMonthOfYear as
        | LegacyMonths
        | undefined;
      for (const chunk of chunks) {
        try {
          if (useDataView) {
            const entries = await getEmployeesForPbsDrillDown(
              chunk,
              endDate,
              firstMonthOfYearLegacy!,
              latestEmployeeVersion,
              cachedMasterDataApiService,
              accessibleFields
            );
            const next: Record<string, EnrichmentCacheEntry> = {};
            for (const e of entries) {
              next[e.id] = e.values;
            }
            Object.assign(cacheForExport, next);
            setEnrichmentCache((prev) => ({ ...prev, ...next }));
          } else {
            const employees = await employeeService.list(
              chunk,
              timeSliderState ?? undefined,
              firstMonthOfYear ?? undefined
            );
            const next: Record<string, EnrichmentCacheEntry> = {};
            for (const e of employees) {
              next[e.id] = employeeToEnrichmentEntry(e);
            }
            Object.assign(cacheForExport, next);
            setEnrichmentCache((prev) => ({ ...prev, ...next }));
          }
          completed += chunk.length;
          setExportProgress({ done: completed, total: missingIds.length });
        } catch {
          completed += chunk.length;
          setExportProgress((prev) =>
            prev
              ? { ...prev, done: completed }
              : { done: completed, total: missingIds.length }
          );
        }
      }
      setIsExporting(false);
      setExportProgress(null);
      downloadCsvWithCache(sortedEmployeeIds, cacheForExport, csvMetricOpts);
    };
    void runBatches().catch(() => {
      setIsExporting(false);
      setExportProgress(null);
    });
  }, [
    sortedEmployeeIds,
    enrichmentCache,
    isExporting,
    downloadCsvWithCache,
    csvMetricOpts,
    useDataViewEnrichment,
    endDate,
    firstMonthOfYear,
    latestEmployeeVersion,
    cachedMasterDataApiService,
    employeeService,
    timeSliderState,
    setEnrichmentCache,
    accessibleFields,
  ]);

  return { isExporting, exportProgress, handleExport };
}
